import jsPDF from "jspdf";

interface FormData {
  businessName: string;
  rut: string;
  address: string;
  businessPhone: string;
  supportPhone: string;
  channels: string[];
}

function addPageHeader(pdf: jsPDF, pageWidth: number) {
  pdf.setFillColor(18, 18, 20);
  pdf.rect(0, 0, pageWidth, 18, "F");
  pdf.setFont("Courier", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text("PRODI-FOOD", 15, 12);
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(142, 142, 147);
  pdf.text("Prodigium Dynamics — Términos y Condiciones", pageWidth - 15, 12, { align: "right" });
}

function checkPageBreak(pdf: jsPDF, yPos: number, needed: number, pageWidth: number, pageHeight: number): number {
  if (yPos + needed > pageHeight - 15) {
    pdf.addPage();
    addPageHeader(pdf, pageWidth);
    return 26;
  }
  return yPos;
}

export async function generateProdifoodPDF(formData: FormData, signatureDataUrl: string) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentW = pageWidth - margin * 2;
  let y = 10;

  // ══════════════════════════════════════════════
  // PORTADA / HEADER
  // ══════════════════════════════════════════════
  pdf.setFillColor(18, 18, 20);
  pdf.rect(0, 0, pageWidth, 52, "F");

  // Título principal
  pdf.setFont("Courier", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(255, 255, 255);
  pdf.text("PRODI-FOOD", margin, y + 12);

  // Subtítulo
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(142, 142, 147);
  pdf.text("Prodigium Dynamics", margin, y + 19);

  // Datos del negocio (derecha del header)
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(255, 255, 255);
  const rx = pageWidth - margin;
  const lh = 4;
  pdf.text(`Negocio:   ${formData.businessName}`,  rx, y + 7,        { align: "right" });
  pdf.text(`RUT:       ${formData.rut}`,            rx, y + 7 + lh,   { align: "right" });
  pdf.text(`Dirección: ${formData.address}`,        rx, y + 7 + lh*2, { align: "right" });
  pdf.text(`Tel. Neg:  ${formData.businessPhone}`,  rx, y + 7 + lh*3, { align: "right" });
  pdf.text(`Tel. Sop:  ${formData.supportPhone}`,   rx, y + 7 + lh*4, { align: "right" });
  if (formData.channels.length > 0) {
    pdf.text(`Canales:   ${formData.channels.join(", ")}`, rx, y + 7 + lh*5, { align: "right" });
  }

  y = 60;

  // ══════════════════════════════════════════════
  // TÍTULO DEL CONTRATO
  // ══════════════════════════════════════════════
  pdf.setFont("Courier", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text("TÉRMINOS Y CONDICIONES DE USO — FASE BETA", margin, y);
  y += 5;
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(80, 80, 80);
  pdf.text("Vigente para: clientes beta de la plataforma Prodi-Food", margin, y);
  y += 9;

  // Línea separadora del título
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.2);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 6;

  // ══════════════════════════════════════════════
  // CLÁUSULAS COMPLETAS
  // ══════════════════════════════════════════════
  const sections: Array<{ title: string; paragraphs: string[] }> = [
    {
      title: "1. QUÉ INCLUYE TU TIENDA",
      paragraphs: [
        "Al activar tu cuenta beta recibes:",
        "• Tu tienda online con link propio, sistema de pedidos y carrito funcional.",
        "• Soporte técnico directo durante toda la beta (vía WhatsApp/email en horario extendido).",
        "• Inscripción en Google Maps: si tu negocio no aparece o tiene datos desactualizados, nosotros lo gestionamos.",
        "• Capacitación básica: te enseñamos a cargar productos, cambiar precios y revisar pedidos.",
        "• Hasta 10 ajustes mensuales sin costo extra: promociones, banners, cambios de texto, fotos de productos o precios.",
        "• Web como PWA (tu negocio estilo app).",
      ],
    },
    {
      title: "2. QUÉ NO INCLUYE (Y CÓMO PEDIRLO)",
      paragraphs: [
        "Lo siguiente no está cubierto en la beta y se cotiza aparte:",
        "• Personalización fuerte de la interfaz: cambios de colores, tipografías, estructura de la página, funciones nuevas o integraciones con sistemas externos.",
        "• Diseño gráfico avanzado: logos, fotografía profesional, videos promocionales.",
        "• Desarrollo a medida: cualquier feature que no exista actualmente en la plataforma.",
        "Si necesitas algo de esto, hablamos y te damos precio antes de tocar código.",
      ],
    },
    {
      title: "3. TUS RESPONSABILIDADES VS LAS NUESTRAS",
      paragraphs: [
        "Tú (el negocio):",
        "• Atender a tus clientes finales. Resolver dudas de pedidos, tiempos de entrega, calidad de comida y pagos.",
        "• Cargar correctamente tu menú, precios y horarios.",
        "• Usar la tienda para fines legales.",
        "Nosotros (Prodigium):",
        "• Mantener la plataforma online, estable y segura.",
        "• Darte soporte técnico cuando algo de la plataforma no funcione.",
        "• Revisar y reparar bugs que afecten el funcionamiento del sistema.",
        "Regla simple: si tu cliente no recibe su pedido, es tu área. Si el botón de \"pedir\" no funciona, es la nuestra. Siempre buscaremos solucionar fallas técnicas, pero no somos responsables por la gestión del negocio ni por conflictos entre tú y tus clientes.",
      ],
    },
    {
      title: "4. TU DOMINIO Y TUS DATOS",
      paragraphs: [
        "Datos de tus clientes:",
        "Toda la información que ingresen los compradores en tu tienda (nombres, teléfonos, direcciones, historial de pedidos) es propiedad exclusiva de tu negocio. Nosotros solo la almacenamos en nuestros servidores como si fuéramos una nube. Nunca la venderemos, compartiremos ni usaremos para otros fines. Si algún día dejas la plataforma, te entregamos tus datos o los eliminamos por completo, como tú prefieras.",
        "Tu dominio:",
        "El dominio de tu tienda (ej. tunegocio.com) es tuyo. Mientras permanezcas en Prodigium, nosotros cubrimos su renovación anual. Si decides salir de la plataforma, puedes llevarte el dominio; deberás hacerse cargo de los costos de transferencia y renovación futuros.",
      ],
    },
    {
      title: "5. PAGO EN FASE BETA",
      paragraphs: [
        "La beta tiene un costo único de 25 USD. No hay mensualidades ni comisiones por venta durante esta fase.",
        "• Ese pago cubre la activación de tu tienda, la configuración del dominio y tu ingreso al grupo de control.",
        "• Como beta tester, tendrás acceso a todas las actualizaciones que le hagamos a la plataforma.",
        "• Tu rol es ser nuestro termómetro directo: nos cuentas qué funciona, qué no y qué necesitas. Eso es lo que pagas con los 25: tu entrada al grupo cerrado.",
      ],
    },
    {
      title: "6. CUÁNDO SUSPENDEMOS EL SERVICIO",
      paragraphs: [
        "Garantizamos el funcionamiento 24/7 y cualquier falla será atendida con prioridad máxima. Sin embargo, podemos suspender o cerrar tu tienda de inmediato si detectamos:",
        "• Venta de productos ilegales, ilícitos o prohibidos.",
        "• Uso de la plataforma para estafar, phishing o cualquier actividad delictual.",
        "• Intentos de dañar el sistema (hackeo, sobrecarga, ingeniería inversa).",
        "• Incumplimiento grave de estos términos.",
        "En esos casos no hay devolución del pago de activación.",
      ],
    },
    {
      title: "7. FALLAS TÉCNICAS: QUÉ CUBRIMOS Y QUÉ NO",
      paragraphs: [
        "Hacemos todo lo posible para que la plataforma esté disponible 24/7, pero somos humanos y la tecnología falla.",
        "• Nuestra responsabilidad: reparar bugs, caídas del servidor o errores de código que impidan el funcionamiento normal.",
        "• No nuestra responsabilidad: pérdida de ventas por un corte de internet en tu zona, que un cliente no pague, que se agote tu stock, o que una red social donde anuncias tu tienda deje de funcionar.",
        "Límite de responsabilidad: en ningún caso Prodigium será responsable por ganancias perdidas, daños indirectos o perjuicios que excedan el monto que pagaste por activación (25 USD).",
      ],
    },
    {
      title: "8. REGLAS DE LA BETA Y EL FUTURO",
      paragraphs: [
        "• Esta fase beta está limitada a 10 negocios. No habrá cambios de precio ni cobros sorpresa durante la beta.",
        "• Cuando la plataforma salga de beta, los beta testers mantendrán condiciones preferenciales.",
        "• Como gesto de que la relación sigue viva, una vez al año nos gustaría que nos invitaras a un platillo de tu menú (o su equivalente). No es un pago obligatorio: es un símbolo de que seguimos creciendo juntos.",
        "No hay letra chica, pero sí reglas claras para que ambos (plataforma y restaurantes) ganemos.",
        "— Prodigium Dynamics · Prodi-Food",
      ],
    },
  ];

  for (const section of sections) {
    // Título de sección
    y = checkPageBreak(pdf, y, 10, pageWidth, pageHeight);
    pdf.setFont("Courier", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text(section.title, margin, y);
    y += 4;

    // Párrafos
    for (const para of section.paragraphs) {
      const lines = pdf.splitTextToSize(para, contentW);
      y = checkPageBreak(pdf, y, lines.length * 3.5 + 1, pageWidth, pageHeight);
      pdf.setFont("Courier", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(30, 30, 30);
      pdf.text(lines, margin, y);
      y += lines.length * 3.5 + 1;
    }
    y += 4;
  }

  // ══════════════════════════════════════════════
  // FIRMA DEL CONTRATO (en la misma página, al final)
  // ══════════════════════════════════════════════
  y = checkPageBreak(pdf, y, 45, pageWidth, pageHeight);

  pdf.setFont("Courier", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text("FIRMA DEL CONTRATO", margin, y);
  y += 5;

  pdf.setFont("Courier", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(80, 80, 80);
  pdf.text(`Negocio: ${formData.businessName}   |   RUT: ${formData.rut}`, margin, y);
  y += 4;
  pdf.text(
    `Fecha: ${new Date().toLocaleDateString("es-CL")}   |   Hora: ${new Date().toLocaleTimeString("es-CL")}`,
    margin, y
  );
  y += 8;

  // Recuadro firma — fondo oscuro para que la firma blanca sea visible
  pdf.setFillColor(28, 28, 30);
  pdf.setDrawColor(80, 80, 80);
  pdf.setLineWidth(0.4);
  pdf.rect(margin, y, contentW, 35, "FD");

  // Insertar firma directamente desde data URL (síncrono en jsPDF)
  try {
    pdf.addImage(signatureDataUrl, "PNG", margin + 2, y + 2, contentW - 4, 31);
  } catch {
    pdf.setFont("Courier", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(120, 120, 120);
    pdf.text("[Firma Digital]", pageWidth / 2, y + 18, { align: "center" });
  }

  y += 40;

  pdf.setFont("Courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(30, 30, 30);
  pdf.text("El firmante declara haber leído y aceptado los Términos y Condiciones de Uso — Fase Beta de Prodi-Food.", margin, y);
  y += 12;

  // Línea de firma
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(margin, y, margin + 70, y);
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(80, 80, 80);
  pdf.text("Firma del representante del negocio", margin, y + 4);
  pdf.text(formData.businessName, margin, y + 8);

  // Footer final
  pdf.setFont("Courier", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(0, 0, 0);
  pdf.text("PRODI-FOOD", pageWidth / 2, pageHeight - 8, { align: "center" });
  pdf.setFont("Courier", "normal");
  pdf.setFontSize(6.5);
  pdf.setTextColor(80, 80, 80);
  pdf.text("Prodigium Dynamics — Contrato Beta", pageWidth / 2, pageHeight - 4, { align: "center" });

  const filename = `Contrato_Prodi-Food_${formData.businessName.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdf.save(filename);
}
